"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { RiskChip } from "./RiskChip";
import { CATEGORIES, type CategoryKey } from "@/lib/library";
import { REPORTS, getReport } from "@/lib/reports";
import { REPORT_TRIGGERS, evaluateTriggers, NOT_SURE, type TriggerQuestion } from "@/lib/triggers";
import { riskScore } from "@/lib/templateContent";
import { track } from "@/lib/analytics";

const DEFAULT_REPORT = "boiler-installation";
const YESNO_LABEL: Record<string, string> = { yes: "Yes", no: "No", [NOT_SURE]: "Not sure" };

const STATUS_META = {
  ready: { label: "Ready for review", dot: "bg-emerald", text: "text-emerald-deep", bg: "bg-emerald-tint" },
  "needs-confirmation": { label: "Needs confirmation", dot: "bg-risk-medium", text: "text-ink", bg: "bg-[#fdf6e3]" },
  draft: { label: "In progress", dot: "bg-risk-high", text: "text-ink", bg: "bg-[#fdeee4]" },
} as const;

function StepLabel({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-wider text-emerald-deep">
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald text-[0.6rem] text-white">{n}</span>
      {label}
    </div>
  );
}

export function PreviewStudio() {
  const [slug, setSlug] = useState(DEFAULT_REPORT);
  const [values, setValues] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const viewedChecker = useRef(false);

  const entry = getReport(slug)!;
  const report = entry.data;
  const triggers = REPORT_TRIGGERS[slug] || [];
  const outcome = useMemo(() => evaluateTriggers(triggers, answers), [triggers, answers]);

  const categories = useMemo(() => CATEGORIES.filter((c) => REPORTS.some((r) => r.category === c.key)), []);
  const categoryReports = useMemo(() => REPORTS.filter((r) => r.category === entry.category), [entry.category]);
  const requiredFields = useMemo(() => report.siteFields.filter((f) => f.required), [report]);
  const visibleQuestions = outcome.visible;
  const previewFields = requiredFields.slice(0, 4);
  const previewQuestions = visibleQuestions.slice(0, 3);
  const hazards = useMemo(
    () => [...report.hazards].sort((a, b) => riskScore(b.initialRisk) - riskScore(a.initialRisk)).slice(0, 5),
    [report],
  );

  const checks = useMemo(() => {
    const fieldChecks = requiredFields.slice(0, 3).map((f) => ({ label: `${f.label} completed`, ok: !!(values[f.key] || "").trim() }));
    const questionCheck = {
      label: `Job questions answered (${Math.min(outcome.answered, visibleQuestions.length)}/${visibleQuestions.length})`,
      ok: outcome.answered >= visibleQuestions.length,
    };
    const residualCheck = { label: "No high residual risks in scope", ok: report.hazards.every((h) => riskScore(h.residualRisk) < 10) };
    return [
      { label: "Hazards and controls loaded", ok: true },
      { label: "Method sequence drafted", ok: true },
      ...fieldChecks,
      questionCheck,
      ...outcome.checks.slice(0, 2),
      residualCheck,
      { label: "Competent-person review before export", ok: false },
    ];
  }, [requiredFields, values, outcome, visibleQuestions.length, report]);

  const ready = checks.filter((c) => c.ok).length;
  const readiness = Math.round((ready / checks.length) * 100);
  const status = STATUS_META[readiness >= 90 ? "ready" : readiness >= 55 ? "needs-confirmation" : "draft"];

  useEffect(() => {
    track("instant_preview_viewed", { report: DEFAULT_REPORT });
    if (!viewedChecker.current) {
      viewedChecker.current = true;
      track("readiness_checker_viewed", { report: DEFAULT_REPORT });
    }
  }, []);

  function selectCategory(key: CategoryKey) {
    if (key === entry.category) return;
    const first = REPORTS.find((r) => r.category === key)!;
    selectReport(first.slug);
    track("preview_category_selected", { category: key });
  }

  function selectReport(nextSlug: string) {
    if (nextSlug === slug) return;
    setSlug(nextSlug);
    setValues({});
    setAnswers({});
    track("builder_report_selected", { report: nextSlug, source: "home_preview" });
    track("instant_preview_viewed", { report: nextSlug });
  }

  function answerQuestion(q: TriggerQuestion, value: string) {
    setAnswers((prev) => ({ ...prev, [q.key]: value }));
    track("builder_question_answered", { report: slug, source: "home_preview", question: q.key, answer: q.kind === "text" ? value.slice(0, 120) : value });
  }

  function startPreview() {
    track("export_intent_clicked", { report: slug, source: "preview_studio" });
  }

  const pillCls = (on: boolean) =>
    `rounded-full px-3 py-1.5 text-[0.78rem] font-semibold transition-colors ${
      on ? "bg-emerald text-white shadow-[0_6px_16px_-8px_rgba(27,163,123,0.8)]" : "border border-line bg-white text-body hover:border-emerald/50 hover:text-ink"
    }`;
  const inputCls =
    "w-full rounded-lg border border-line bg-white px-3 py-2 text-[0.8rem] text-ink outline-none transition-colors placeholder:text-muted/60 focus:border-emerald focus:ring-2 focus:ring-emerald/15";

  function renderQuestion(q: TriggerQuestion, depth = 0): React.ReactNode {
    const answer = answers[q.key] || "";
    return (
      <div key={q.key} className={depth > 0 ? "ml-3 border-l-2 border-emerald/30 pl-3" : ""}>
        <p className="text-[0.76rem] font-semibold leading-snug text-ink">{q.label}</p>
        {q.kind === "yesno" && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {["yes", "no", ...(q.notSure ? [NOT_SURE] : [])].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => answerQuestion(q, v)}
                aria-pressed={answer === v}
                className={`rounded-full border px-3 py-1.5 text-[0.72rem] font-semibold transition-colors ${
                  answer === v ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-emerald/50"
                }`}
              >
                {YESNO_LABEL[v]}
              </button>
            ))}
          </div>
        )}
        {q.kind === "choice" && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {(q.options || []).map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => answerQuestion(q, o)}
                aria-pressed={answer === o}
                className={`rounded-full border px-3 py-1.5 text-[0.72rem] font-semibold transition-colors ${
                  answer === o ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-emerald/50"
                }`}
              >
                {o}
              </button>
            ))}
          </div>
        )}
        {q.kind === "text" && (
          <input
            type="text"
            value={answer}
            onChange={(e) => answerQuestion(q, e.target.value)}
            placeholder={q.placeholder}
            className={`mt-1.5 ${inputCls}`}
          />
        )}
        {q.followUps && answer === q.followUps.when && (
          <div className="mt-2.5 space-y-2.5">{q.followUps.questions.slice(0, 2).map((f) => renderQuestion(f, depth + 1))}</div>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-white text-ink shadow-float ring-1 ring-black/5">
      <div className="border-b border-line bg-mist px-4 py-3.5 sm:px-5">
        <StepLabel n={1} label="Choose a category" />
        <div className="mt-2.5 flex flex-wrap gap-1.5" role="tablist" aria-label="Choose a category">
          {categories.map((c) => (
            <button key={c.key} type="button" role="tab" aria-selected={c.key === entry.category} onClick={() => selectCategory(c.key)} className={pillCls(c.key === entry.category)}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <StepLabel n={2} label="Choose your report" />
          <div className="mt-2.5 flex flex-wrap gap-1.5" role="tablist" aria-label="Choose your report">
            {categoryReports.map((r) => (
              <button key={r.slug} type="button" role="tab" aria-selected={r.slug === slug} onClick={() => selectReport(r.slug)} className={pillCls(r.slug === slug)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
        <div className="px-4 py-4 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-emerald-deep">{entry.trade} · {entry.category.replace(/-/g, " ")}</p>
              <p className="mt-0.5 font-display text-lg font-bold text-ink">{report.title}</p>
              <p className="mt-0.5 text-[0.72rem] text-muted">Starting point based on the selected report</p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.66rem] font-semibold ${status.bg} ${status.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} /> {status.label}
            </span>
          </div>

          <div className="mt-3 rounded-lg bg-mist px-3 py-2">
            <div className="flex items-center justify-between text-[0.7rem] font-medium text-ink">
              <span>Readiness</span>
              <span className="tabular-nums text-emerald-deep">{readiness}% ready</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
              <div className="h-full rounded-full bg-emerald transition-[width] duration-300" style={{ width: `${readiness}%` }} />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-[1fr_auto_auto] gap-x-3 border-b border-line pb-1.5 text-[0.6rem] font-semibold uppercase tracking-wider text-muted">
            <span>Hazard</span>
            <span className="text-center">Initial</span>
            <span className="text-center">Residual</span>
          </div>
          <div>
            {hazards.map((h) => (
              <div key={h.hazardId} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 border-b border-line py-2 text-[0.8rem]">
                <span className="leading-snug text-body">{h.hazard}</span>
                <span className="flex justify-center"><RiskChip rating={h.initialRisk} /></span>
                <span className="flex justify-center"><RiskChip rating={h.residualRisk} /></span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[0.66rem] text-muted">Risk 1-25 · likelihood x severity · controls applied for residual</p>
        </div>

        <div className="border-t border-line bg-mist/60 px-4 py-4 sm:px-5 lg:border-l lg:border-t-0">
          <StepLabel n={3} label="Site details" />
          <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
            {previewFields.map((f) => (
              <label key={f.key} className="block">
                <span className="mb-1 block text-[0.66rem] font-semibold text-body">
                  {f.label} <span className="text-risk-high">*</span>
                </span>
                <input value={values[f.key] || ""} onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))} className={inputCls} />
              </label>
            ))}
          </div>

          <div className="mt-4">
            <StepLabel n={4} label="Job questions" />
            <div className="mt-2.5 space-y-3">
              {previewQuestions.length ? previewQuestions.map((q) => renderQuestion(q)) : <p className="text-[0.74rem] text-muted">No extra job questions for this report.</p>}
            </div>
          </div>

          <div className="mt-4">
            <StepLabel n={5} label="Pre-submission checks" />
            <ul className="mt-2.5 space-y-1.5">
              {checks.slice(0, 7).map((c) => (
                <li key={c.label} className="flex items-start gap-2 text-[0.72rem] leading-snug">
                  {c.ok ? (
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald text-white">
                      <svg viewBox="0 0 20 20" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3"><path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </span>
                  ) : (
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-risk-medium text-ink">
                      <svg viewBox="0 0 20 20" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3"><path d="M10 5v6M10 14.5v.5" strokeLinecap="round" /></svg>
                    </span>
                  )}
                  <span className={c.ok ? "text-muted line-through decoration-line-2" : "font-medium text-ink"}>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-line bg-white px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <p className="text-[0.7rem] leading-snug text-muted">
          Opens the full builder with this report selected. You review and approve as the competent person.
        </p>
        <a
          href={`/builder/?report=${slug}`}
          onClick={startPreview}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-emerald px-4 py-2 text-[0.82rem] font-semibold text-white transition-colors hover:bg-emerald-deep"
        >
          Build this RAMS →
        </a>
      </div>
    </div>
  );
}
